import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Circle, Clapperboard, Hourglass, ImagePlus, Loader2, Play, RotateCcw, Tv } from "lucide-react";
import { toast } from "sonner";
import { FolderTabs } from "@/components/FolderTabs";
import { SelectionBar } from "@/components/SelectionBar";
import { SelectMenu, BarButton, LassoArea, useSelection } from "@/components/Selection";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { VideoCard, VideoThumb, fmtClock } from "@/components/VideoBits";
import { PosterFinder, useCanPoster } from "@/components/PosterFinder";
import { TmdbDetails } from "@/components/TmdbDetails";

/* One show (seasons → episodes) or one folder collection (movies as 2:3 poster cards). */
export default function VideoGroup() {
  const { key } = useParams();
  const [params, setParams] = useSearchParams();
  const canPoster = useCanPoster();
  const [data, setData] = useState(null);
  const [finder, setFinder] = useState(null); // { target, title, hasPoster }
  const load = useCallback(() => api.get("/videos").then((r) => setData(r.data)).catch(() => setData({ groups: [] })), []);
  useEffect(() => { load(); }, [load]);
  const g = data?.groups.find((x) => x.key === key);
  const seasons = useMemo(() => {
    if (!g) return [];
    const by = new Map();
    g.videos.forEach((v) => { const k = v.season ?? 0; if (!by.has(k)) by.set(k, []); by.get(k).push(v); });
    return [...by.entries()].sort((a, b) => a[0] - b[0]);
  }, [g]);
  const next = g?.videos.find((v) => v.playable && !v.progress.finished && v.progress.pct > 0) || g?.videos.find((v) => v.playable && !v.progress.finished) || g?.videos.find((v) => v.playable);
  const vidIds = useMemo(() => seasons.flatMap(([, vids]) => vids.map((v) => v.id)), [seasons]);
  const s = useSelection(vidIds);
  const [busy, setBusy] = useState(false);
  const bulk = async (action) => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/videos/bulk", { ids: [...s.sel], action });
      toast.success(`Marked ${r.count} video${r.count === 1 ? "" : "s"} ${action}`);
      s.exit(); load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  if (!data) return <Loader2 className="animate-spin text-zinc-400" />;
  if (!g) return <EmptyState icon={Clapperboard} title="Not found" text="This show or collection isn't in the library any more." testid="video-group-missing" />;
  const isShow = g.kind === "show";
  const all = g.videos;
  const seasonNums = seasons.map(([n]) => n);
  const wanted = params.get("season");
  const season = wanted !== null && seasonNums.includes(Number(wanted)) ? Number(wanted) : (next && seasonNums.includes(next.season ?? 0) ? next.season ?? 0 : seasonNums[0]);
  const picks = [
    { label: "Select watched", icon: CheckCircle2, testid: "watched", count: all.filter((v) => v.progress.finished).length, onClick: () => s.where((v) => v.progress.finished, all) },
    { label: "Select in progress", icon: Hourglass, testid: "in-progress", count: all.filter((v) => !v.progress.finished && v.progress.pct > 0).length, onClick: () => s.where((v) => !v.progress.finished && v.progress.pct > 0, all) },
    { label: "Select unwatched", icon: Circle, testid: "unwatched", count: all.filter((v) => !v.progress.finished && !(v.progress.pct > 0)).length, onClick: () => s.where((v) => !v.progress.finished && !(v.progress.pct > 0), all) },
    ...seasons.filter(([n]) => isShow && n).map(([n, vids]) => ({ label: `Select season ${n}`, icon: Tv, testid: `season-${n}`, count: vids.length, onClick: () => s.where((v) => (v.season ?? 0) === n, all) })),
  ];
  const openShowPoster = () => setFinder({ target: { kind: "show", key: g.name }, title: g.name, hasPoster: !!g.poster });
  const openMoviePoster = (v) => setFinder({ target: { kind: "video", key: v.id }, title: v.title, hasPoster: !!v.poster });
  return (
    <div data-testid="video-group-page">
      <Link to="/videos" className="mb-4 inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900" data-testid="video-group-back"><ArrowLeft size={12} />Videos</Link>
      <div className={isShow ? "flex flex-col gap-5 sm:flex-row sm:items-end" : ""}>
        {isShow && (
          <div className="relative w-32 shrink-0 sm:w-40" data-testid="video-group-poster">
            <VideoThumb id={g.cover_id} poster={g.poster} portrait className="shadow-xl" />
            {canPoster && (
              <button type="button" onClick={openShowPoster} title="Change poster" data-testid="video-group-change-poster"
                className="absolute bottom-2 right-2 flex h-8 w-8 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur hover:bg-black/80"><ImagePlus size={14} /></button>
            )}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <PageHeader stack eyebrow={`${isShow ? "Show" : "Collection"} · ${g.count} video${g.count === 1 ? "" : "s"}${g.duration ? ` · ${fmtClock(g.duration)}` : ""}`} title={g.tmdb?.title || g.name} testid="video-group-header">
            {next && <Link to={`/videos/watch/${next.id}`} data-testid="video-group-play" className="flex h-11 items-center gap-2 rounded-full bg-zinc-900 px-5 text-sm font-medium text-white transition-transform hover:scale-[1.03]"><Play size={15} fill="currentColor" />{next.progress.pct > 0 ? "Continue" : "Play"}</Link>}
            <SelectMenu s={s} noun={isShow ? "episode" : "video"} testid="episodes-select" picks={picks} />
          </PageHeader>
          {isShow && g.tmdb && <div className="-mt-4 mb-6"><TmdbDetails d={g.tmdb} compact testid="show-details" /></div>}
        </div>
      </div>
      {isShow && seasons.length > 1 ? (
        <div className="mt-6">
        <FolderTabs testid="season-tabs" value={String(season)} onChange={(v) => setParams((p) => { p.set("season", v); return p; }, { replace: true })}
          tabs={seasons.map(([sn, vids]) => ({ id: String(sn), label: sn ? `Season ${sn}` : "Specials", icon: Tv, badge: vids.length }))}>
          {(id) => {
            const vids = seasons.find(([sn]) => String(sn) === id)?.[1] || [];
            return (
              <LassoArea s={s} testid={`episodes-lasso-${id}`}>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5" data-testid={`video-grid-${id}`}>
                  {vids.map((v) => <VideoCard key={v.id} v={v} sel={s} />)}
                </div>
              </LassoArea>
            );
          }}
        </FolderTabs>
        </div>
      ) : (
      <LassoArea s={s} testid="episodes-lasso">{seasons.map(([sn, vids]) => (
        <section key={sn} className="mb-8" data-testid={`video-season-${sn}`}>
          {isShow && <h2 className="mb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-400">{sn ? `Season ${sn}` : "Specials"}</h2>}
          <div className={isShow ? "grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5" : "grid grid-cols-3 gap-3 sm:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6"} data-testid={`video-grid-${sn}`}>
            {vids.map((v) => <VideoCard key={v.id} v={v} portrait={!isShow} sel={s} onPoster={!isShow && canPoster && !s.mode ? openMoviePoster : undefined} />)}
          </div>
        </section>
      ))}</LassoArea>
      )}
      <SelectionBar count={s.count} hidden={s.hidden} noun={isShow ? "episode" : "video"} busy={busy} onClear={s.none} onSelectAll={!s.allShown ? s.all : null}>
        <BarButton onClick={() => bulk("watched")} icon={CheckCircle2} testid="selection-mark-watched" disabled={busy}>Mark watched</BarButton>
        <BarButton onClick={() => bulk("unwatched")} icon={RotateCcw} testid="selection-mark-unwatched" disabled={busy}>Mark unwatched</BarButton>
      </SelectionBar>
      <PosterFinder target={finder?.target} title={finder?.title} hasPoster={finder?.hasPoster} open={!!finder} onOpenChange={(o) => { if (!o) setFinder(null); }} onApplied={() => { load(); setTimeout(load, 2500); }} />
    </div>
  );
}
